import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Placeholder,
} from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import fileUploader from "../../../../utilities/fileUploader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  HOTELLIST,
  ROOMADD,
  ROOMESSENTAILLIST,
  ROOMLIST,
  ROOMTYPE,
  ROOMUPDATE,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";

const roomSchema = z.object({
  hotelId: z.string().nonempty("Hotel is required"),
  amenities: z.array(z.string()).min(1, "Select at least one amenity"),
  floorNumber: z.coerce.number().min(1, "Floor number is required"),
  roomNumber: z.coerce.number().min(1, "Room number is required"),
  images: z.array(z.string()).min(1, "At least one image is required"),
  numberOfBed: z.coerce.number().min(1, "Number of beds is required"),
  type: z.string().nonempty("Room type is required"),
  checkInHour: z.string().min(1, "Check-in hour is required"),
  checkOutHour: z.string().min(1, "Check-out hour is required"),
  status: z.enum(["Available", "Unavailable"], {
    required_error: "Status is required",
  }),
  maxOccupancy: z.coerce
    .number()
    .int("Max occupancy must be an integer")
    .min(1, "Number of people must be greater than 0"),

  length: z.coerce
    .number()
    .int("Length must be an integer")
    .min(1, "Length is required"),

  width: z.coerce
    .number()
    .int("Width must be an integer")
    .min(1, "Width is required"),
  housekeepingNotes: z.string().optional(),

  pricing: z
    .array(
      z.object({
        durationType: z.string().nonempty("Duration type is required"),
        basePrice: z.coerce.number().min(0, "Base price must be positive"),
        weekRate: z.coerce.number().min(0, "Week rate cannot be negative"),
        discount: z
          .number()
          .min(0, { message: "Discount cannot be negative" })
          .max(100, { message: "Discount cannot exceed 100%" }),
        activated: z.boolean().optional(),
        refundable: z.boolean().optional(),
        date: z.string().min(1, "Date is required"),
      })
    )
    .min(1, "At least one pricing entry is required"),
});

const AddRoom = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;

  const [imagePreviews, setImagePreviews] = useState([]);
  const [imgloader, setImgLoader] = useState(false);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [hotelAdminData, setHotelData] = useState([]);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [hotelDetailData, setHotelDetailData] = useState([]);
  const [loader, setLoader] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      hotelId: "",
      amenities: [],
      floorNumber: "",
      roomNumber: "",
      images: [],
      numberOfBed: "",
      type: "",
      width: "",
      length: "",
      maxOccupancy: "",
      checkInHour: "",
      checkOutHour: "",
      status: "Available",
      housekeepingNotes: "",
      pricing: [
        {
          durationType: "",
          basePrice: 0,
          discount: 0,
          weekRate: 0,
          activated: true,
          refundable: true,
          date: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "pricing",
  });

  const getAmenityData = catchAsync(async () => {
    const [roomRes, hotelRes, roomTypeRes] = await Promise.all([
      ROOMESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      HOTELLIST({ limit: 100, page: 1, status: "Active" }),
      ROOMTYPE({ limit: 100, page: 1, status: "Active" }),
    ]);

    checkResponse({ res: roomRes, setData: setRoomAmenities });
    checkResponse({ res: hotelRes, setData: setHotelData });
    checkResponse({ res: roomTypeRes, setData: setRoomTypeData });
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMLIST({ id });
    const success = checkResponse({
      res,
      setData: setHotelDetailData,
      setLoader,
    });

    if (success && res?.data?.data) {
      const data = res.data.data;
      console.log(data, "data===<>come");
      setImagePreviews(data.images || []);
      reset({
        hotelId: data.hotelId?._id || "",
        amenities: data.amenities?.map((a) => a._id) || [],
        floorNumber: data.floorNumber || "",
        roomNumber: data.roomNumber || "",
        numberOfBed: data.numberOfBed || "",
        type: data.type?._id || "",
        status: data.status || "Available",
        checkInHour: data?.checkInHour || "",
        checkOutHour: data?.checkOutHour || "",
        maxOccupancy: data?.maxOccupancy || "",
        length: data?.length || "",
        width: data?.width || "",
        housekeepingNotes: data.housekeepingNotes || "",
        images: data.images || [],
        pricing: data.pricing?.length
          ? data.pricing
          : [
              {
                durationType: "",
                basePrice: 0,
                weekRate: 0,
                discount: 0,
                activated: true,
                refundable: true,
              },
            ],
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const handleCheckbox = (field, value, checked) => {
    const currentValues = watch("amenities") || [];
    const updated = checked
      ? [...currentValues, value]
      : currentValues.filter((id) => id !== value);
    setValue("amenities", updated, { shouldValidate: true });
  };

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);
    const files = Array.from(e.target.files);
    const uploadedUrls = [];

    for (const file of files) {
      const res = await fileUploader(file);
      if (res?.url) uploadedUrls.push(res.url);
    }

    const updated = [...(watch("images") || []), ...uploadedUrls];
    setValue("images", updated, { shouldValidate: true });
    setImagePreviews(updated);
    setImgLoader(false);
  });

  const handleRemoveImage = (index) => {
    const updated = (watch("images") || []).filter((_, i) => i !== index);
    setValue("images", updated, { shouldValidate: true });
    setImagePreviews(updated);
  };

  const submitHandler = catchAsync(async (data) => {
    const body = { ...data, ...(id && { _id: id }) };
    console.log(body, "body ====<>");
    const res = await (id ? ROOMUPDATE(body) : ROOMADD(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/room?page=${page}`);
  });

  const handleCancel = () => navigate(`/room?page=${page}`);

  console.log(hotelAdminData, "hotelAdminData===");
  return (
    <>
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/room?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("manageRoom.editRoom") : t("manageRoom.addRoom")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.selectHotel")} </label>
                      <select className="form-control" {...register("hotelId")}>
                        <option value="">-- Select Hotel --</option>
                        {hotelAdminData?.hotelList?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.hotelName}
                          </option>
                        ))}
                      </select>

                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.floorNumber")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("floorNumber", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.floorNumber && (
                        <p className="text-danger">
                          {errors.floorNumber.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.roomNumber")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("roomNumber", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.roomNumber && (
                        <p className="text-danger">
                          {errors.roomNumber.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.numberOfBed")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("numberOfBed", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.numberOfBed && (
                        <p className="text-danger">
                          {errors.numberOfBed.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.numberOfPeople", "People Allowed")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxOccupancy && (
                        <p className="text-danger">
                          {errors.maxOccupancy.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.roomLength", "Room Length (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("length", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.length && (
                        <p className="text-danger">{errors.length.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.roomWidth", "Room Width (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("width", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.width && (
                        <p className="text-danger">{errors.width.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label> {t("manageRoom.roomType")} </label>
                      <select className="form-control" {...register("type")}>
                        <option value="">-- Select Room Type --</option>
                        {roomTypeData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                      {errors?.type && (
                        <p className="text-danger">{errors.type.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageHotel.checkInHour", "CheckInHour")}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkInHour")}
                      />
                      {errors?.checkInHour && (
                        <p className="text-danger">
                          {errors.checkInHour.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageHotel.checkOutHour", "CheckOutHour")}
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("checkOutHour")}
                      />
                      {errors?.checkOutHour && (
                        <p className="text-danger">
                          {errors.checkOutHour.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.status")}</label>
                      <Form.Select {...register("status")}>
                        <option value="Available">Available</option>
                        <option value="Unavailable">Unavailable</option>
                        <option value="Maintenance">Maintenance</option>
                      </Form.Select>
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>{t("manageRoom.housekeepingNotes")}</label>
                      <textarea
                        rows={3}
                        className="form-control"
                        {...register("housekeepingNotes")}
                      />
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">Room Amenities</label>
                      <div
                        className="d-flex flex-wrap gap-3 p-2 border rounded"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {roomAmenities?.length > 0
                          ? roomAmenities.map((amenity) => (
                              <div
                                key={amenity._id}
                                className="d-flex align-items-center"
                                style={{ minWidth: "150px" }}
                              >
                                <Form.Check
                                  type="checkbox"
                                  label={amenity.name}
                                  checked={watch("amenities")?.includes(
                                    amenity._id
                                  )}
                                  onChange={(e) =>
                                    handleCheckbox(
                                      "amenities",
                                      amenity._id,
                                      e.target.checked
                                    )
                                  }
                                />
                              </div>
                            ))
                          : Array.from({ length: 6 }).map((_, idx) => (
                              <Placeholder key={idx} xs={8} bg="secondary" />
                            ))}
                      </div>
                      {errors.amenities && (
                        <p className="text-danger">
                          {errors.amenities.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-bold text-muted ps-2 m-0">
                        Upload Images
                      </label>
                      <div
                        className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                        style={{ cursor: "pointer", minHeight: "140px" }}
                        onClick={() =>
                          document.getElementById("imageUploadInput").click()
                        }
                      >
                        <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                        <p className="m-0 text-muted">
                          Click or Drag & Drop to Upload
                        </p>
                        <input
                          id="imageUploadInput"
                          type="file"
                          accept="image/*"
                          multiple
                          className="d-none"
                          onChange={handleImageChange}
                        />
                      </div>

                      {errors.images && (
                        <p className="text-danger mt-1">
                          {errors.images.message}
                        </p>
                      )}

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {imgloader ? (
                          <div
                            style={{ height: 120, width: 120 }}
                            className="d-flex align-items-center justify-content-center border rounded"
                          >
                            Loading...
                          </div>
                        ) : (
                          imagePreviews.map((src, i) => (
                            <div
                              key={i}
                              style={{
                                width: 120,
                                height: 120,
                                position: "relative",
                              }}
                              className="border rounded overflow-hidden shadow-sm"
                            >
                              <img
                                src={src}
                                alt={`preview-${i}`}
                                className="w-100 h-100 object-fit-cover"
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                onClick={() => handleRemoveImage(i)}
                              >
                                ✕
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div
                        className="formWrpper rounded"
                        style={{ background: "#EEEEEE" }}
                      >
                        <span className="fw-bold mb-2 d-block">
                          {t("manageRoom.addPrice")}
                        </span>

                        <Row>
                          <Col lg="12" className="my-2">
                            <h6>{t("manageRoom.ratePricing")}</h6>
                            {fields.map((field, index) => (
                              <Row
                                key={field.id}
                                className="align-items-center mb-3"
                              >
                                <Col md={3}>
                                  <label>{t("manageRoom.durationType")}</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    {...register(
                                      `pricing.${index}.durationType`
                                    )}
                                  />
                                  {errors.pricing?.[index]?.durationType && (
                                    <p className="text-danger">
                                      {
                                        errors.pricing[index].durationType
                                          .message
                                      }
                                    </p>
                                  )}
                                </Col>

                                <Col md={2}>
                                  <label>{t("manageRoom.basePrice")}</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.basePrice`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.basePriceNegative"
                                        ),
                                      },
                                    })}
                                    min={0}
                                  />
                                  {errors.pricing?.[index]?.basePrice && (
                                    <p className="text-danger">
                                      {errors.pricing[index].basePrice.message}
                                    </p>
                                  )}
                                </Col>

                                <Col md={2}>
                                  <label>
                                    {t("manageRoom.weekRate", "Weekend Rate")}
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.weekRate`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.discountNegative"
                                        ),
                                      },
                                      // max: {
                                      //   value: 100,
                                      //   message: t("validation.discountExceed"),
                                      // },
                                    })}
                                    min={0}
                                    // max={100}
                                  />
                                  {errors.pricing?.[index]?.weekRate && (
                                    <p className="text-danger">
                                      {errors.pricing[index].weekRate.message}
                                    </p>
                                  )}
                                </Col>
                                <Col md={2}>
                                  <label>
                                    {t("manageRoom.discount", "Discount")}
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    {...register(`pricing.${index}.discount`, {
                                      valueAsNumber: true,
                                      min: {
                                        value: 0,
                                        message: t(
                                          "validation.discountNegative"
                                        ),
                                      },
                                      max: {
                                        value: 100,
                                        message: t("validation.discountExceed"),
                                      },
                                    })}
                                    min={0}
                                    max={100}
                                  />
                                  {errors.pricing?.[index]?.discount && (
                                    <p className="text-danger">
                                      {errors.pricing[index].discount.message}
                                    </p>
                                  )}
                                </Col>
                                <Col md={2}>
                            <label>Date</label>
                            <input
                              type="date"
                              className="form-control"
                             
                              {...register(`pricing.${index}.date`)}
                            />
                            {errors.pricing?.[index]?.date && (
                              <p className="text-danger">
                                {errors.pricing[index].date?.message}
                              </p>
                            )}
                          </Col>

                                <Col
                                  md={2}
                                  className="d-flex align-items-center flex-column py-2 justify-content-start"
                                >
                                  <Form.Check
                                    type="checkbox"
                                    label={t(
                                      "manageRoom.activated",
                                      "Activated"
                                    )}
                                    {...register(`pricing.${index}.activated`)}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label={t("manageRoom.refundable")}
                                    {...register(`pricing.${index}.refundable`)}
                                  />
                                </Col>

                                {/* <Col
                                  md={2}
                                  className="d-flex align-items-center"
                                >
                                  <Form.Check
                                    type="checkbox"
                                    label={t("manageRoom.refundable")}
                                    {...register(`pricing.${index}.refundable`)}
                                  />
                                </Col> */}

                                <Col md={1}>
                                  <Button
                                    variant="danger"
                                    onClick={() => remove(index)}
                                  >
                                    {t("common.remove")}
                                  </Button>
                                </Col>
                              </Row>
                            ))}

                            <Button
                              variant="primary"
                              onClick={() =>
                                append({
                                  durationType: "",
                                  basePrice: 0,
                                  weekRate: 0,
                                  activated: true,
                                  refundable: true,
                                  date: "",
                                })
                              }
                            >
                              {t("common.addRate")}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoom;
